import React from 'react';
import { StatusAlertSmall } from '@wix/wix-ui-icons-common';

import Input from '../Input';
import LabelledElement from '../LabelledElement/LabelledElement';
import Text from '../Text';

import InputWithOptions from '../InputWithOptions';
import { st, classes } from './AutoCompleteWithLabel.st.css';

import { DATA_HOOKS } from './AutoCompleteWithLabel.constants';
import { AutoCompleteWithLabelProps } from './AutoCompleteWithLabel.types';
import type {
  DropdownLayoutOption,
  DropdownLayoutValueOption,
} from '../DropdownLayout';

interface AutoCompleteWithLabelState {
  value: string | number;
  isEditing: boolean;
}

class AutoCompleteWithLabel extends React.PureComponent<
  AutoCompleteWithLabelProps,
  AutoCompleteWithLabelState
> {
  static displayName = 'AutoCompleteWithLabel';
  static defaultProps = {
    autocomplete: 'on',
    label: '',
    options: [],
    onSelect: () => {},
  };

  constructor(props: AutoCompleteWithLabelProps) {
    super(props);

    this.state = {
      value: props.value || '',
      isEditing: false,
    };
  }

  onSelect = (option: DropdownLayoutValueOption) => {
    if (!option) {
      return;
    }
    const { value } = option;
    this.setState({ value: value as string | number });
    this.props.onSelect!(option);
    this.setState({ isEditing: false });
  };

  onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    this.setState({ value, isEditing: true });
    this.props.onChange && this.props.onChange(event);
  };

  _isInputControlled = () => typeof this.props.value !== 'undefined';

  render() {
    const {
      label,
      dataHook,
      options,
      status,
      suffix,
      statusMessage,
      onFocus,
      name,
      type,
      ariaLabel,
      autoFocus,
      autocomplete,
      disabled,
      className,
      maxLength,
      placeholder,
      native,
      onBlur,
      maxHeightPixels,
    } = this.props;
    const { value } = this._isInputControlled() ? this.props : this.state;
    const predicate = this.state.isEditing
      ? (option: DropdownLayoutOption) =>
          typeof option.value === 'string' &&
          option.value.toLowerCase().includes((value as string).toLowerCase())
      : () => true;
    const filteredOptions = options!.filter(predicate);

    const suffixContainer = suffix
      ? suffix.map((item, index) => {
          return (
            <div className={classes.suffix} key={`${dataHook}-${index}`}>
              {item}
            </div>
          );
        })
      : [];

    return (
      <div data-hook={dataHook}>
        <LabelledElement
          label={label!}
          dataHook={DATA_HOOKS.labelledElement}
          value={value}
        >
          {({
            onFocus: inputOnFocus,
            onBlur: inputOnBlur,
            onChange: inputOnChange,
            className: inputClassName,
            getPlaceholder,
            ...rest
          }) => (
            <InputWithOptions
              onChange={e => {
                inputOnChange(e);
                this.onChange(e);
              }}
              onSelect={this.onSelect}
              dataHook={DATA_HOOKS.inputWithOptions}
              hideStatusSuffix
              onFocus={e => {
                inputOnFocus();
                onFocus?.(e);
              }}
              onBlur={e => {
                inputOnBlur();
                onBlur?.(e);
              }}
              size="large"
              autocomplete={autocomplete}
              inputElement={
                <Input
                  name={name}
                  type={type}
                  ariaLabel={ariaLabel}
                  autoFocus={autoFocus}
                  disabled={disabled}
                  maxLength={maxLength}
                  placeholder={getPlaceholder(placeholder)}
                  dataHook={DATA_HOOKS.inputWithLabel}
                  value={value}
                  className={st(inputClassName, className)}
                  suffix={suffixContainer}
                  status={status}
                />
              }
              options={filteredOptions}
              native={native}
              maxHeightPixels={maxHeightPixels}
              {...rest}
            />
          )}
        </LabelledElement>
        {status === Input.StatusError && statusMessage && (
          <Text
            skin="error"
            weight="normal"
            size="small"
            className={classes.statusMessage}
          >
            <span className={classes.statusMessageIcon}>
              <StatusAlertSmall />
            </span>
            <span
              data-hook={DATA_HOOKS.errorMessage}
              className={classes.errorMessageContent}
            >
              {statusMessage}
            </span>
          </Text>
        )}
      </div>
    );
  }
}

export default AutoCompleteWithLabel;
